import { AxiosResponse } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { PrivilegesList as ModelPrivilegesList } from '../../../../api/model';
import * as sessionStorageUtils from '../../../../utils/sessionStorage';
import PageNavi from '../../../modules/PageNavi';

const N_DATA_PER_PAGE = 50;

interface Props {
  data: AxiosResponse<ModelPrivilegesList, unknown> | undefined;
  isLoading: boolean;
  isError: boolean;
}

function PrivilegesList({ data, isLoading, isError }: Props) {
  const { t, i18n } = useTranslation();
  const [currentPage, setCurrentPage] = useState(sessionStorageUtils.get('Privileges.CurrentPage', 1));
  const [sortInfo, setSortInfo] = useState(
    sessionStorageUtils.get('Privileges.SortInfo', { key: 'sort_order', isDisc: true })
  );

  const refIsFirstLoad = useRef(true);
  const allRecord = useMemo(
    () =>
      [...(data?.data.privileges_list_items ?? [])].sort((a, b) => {
        const aAny = a as { [key: string]: string | undefined };
        const bAny = b as { [key: string]: string | undefined };
        const multiValue = sortInfo.isDisc ? -1 : 1;
        if (aAny[sortInfo.key] !== bAny[sortInfo.key]) {
          // 第一ソートキー
          return ((aAny[sortInfo.key] ?? '') > (bAny[sortInfo.key] ?? '') ? 1 : -1) * multiValue;
        }
        // if (a.privilege_id !== b.privilege_id) {
        //   // 第二ソートキー
        //   return ((a.privilege_name ?? '') > (b.privilege_name ?? '') ? 1 : -1) * multiValue;
        // }
        // 第三ソートキー
        return ((a.sort_order ?? '') > (b.sort_order ?? '') ? 1 : -1) * multiValue;
      }),
    [sortInfo, data]
  );
  const filteredRecord = useMemo(
    () => allRecord.slice(N_DATA_PER_PAGE * (currentPage - 1), N_DATA_PER_PAGE * currentPage),
    [currentPage, allRecord]
  );
  const changeSortKey = useCallback((key: string) => {
    setSortInfo((pre) => {
      const next = { key, isDisc: pre.key === key && !pre.isDisc };
      sessionStorageUtils.set('Privileges.SortInfo', next);
      return next;
    });
    setCurrentPage(1);
  }, []);
  const changeCurrentPage = useCallback(
    (next: number) => {
      sessionStorageUtils.set('Privileges.CurrentPage', next);
      setCurrentPage(next);
    },
    [setCurrentPage]
  );

  useEffect(() => {
    if (data?.data.privileges_list_items != null) {
      if (refIsFirstLoad.current) {
        refIsFirstLoad.current = false;
      } else {
        setCurrentPage(1);
      }
    }
  }, [data?.data.privileges_list_items]);

  if (isLoading) {
    return <div>{t('お待ちください。')}</div>;
  }

  if (isError || data == null || data.data.privileges_list_items == null) {
    return <div>{t('エラーが発生しました。')}</div>;
  }

  return (
    <div className="wfr in-pgnavi documentlist">
      <div className="scroll">
        <table className="table1b">
          <tbody>
            <tr>
              <th>
                {t('権限名')}
                <i
                  className="fa-solid fa-sort"
                  onClick={() => {
                    changeSortKey('privilege_name');
                  }}
                />
              </th>
              <th>
                {t('メモ')}
                <i
                  className="fa-solid fa-sort"
                  onClick={() => {
                    changeSortKey('memo');
                  }}
                />
              </th>
            </tr>
            {filteredRecord.map((x) => (
              <tr key={x.privilege_id}>
                <td>
                  <Link to={`/${i18n.language}/privileges/edit/${x.privilege_id?.toString() ?? ''}`}>
                    {' '}
                    {x.privilege_name}
                  </Link>
                </td>
                <td>{x.memo}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PageNavi
        current={currentPage}
        pageCount={Math.ceil(data.data.privileges_list_items.length / (N_DATA_PER_PAGE * 1.0))}
        onChangePage={changeCurrentPage}
      />
    </div>
  );
}

export default PrivilegesList;
